import React, { useEffect, useRef, useState } from 'react';
import { Box, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Button as MuiButton } from '@material-ui/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { Collapse } from '@material-ui/core';
import { Stepper, Step, StepLabel } from '@material-ui/core';
import termoEspirometriaPDF from '../Termo/termo-consentimento-programa-sopro-GSK.pdf';
import termoHemoCuePDF from '../Termo/termo-consentimento-hemocue.pdf';

import {
  GetForms,
  GetProgramsAccreditedsByProgramId,
  RegisterFormAnswers,
  ProgramsPatients,
  ProgramsAccrediteds,
  RegisterFormAnswersParams,
  GetPatientByCpf,
  GetPatientByRg,
  GetProgramsPatientsByProgramIdRg,
  GetProgramsPatientsByProgramIdCpf
} from '@bforms-web/common';

import { FORMS_ROUTE } from '../../../../../constants';
import { useLoading } from '../../../../../contexts/loading.context';
import Button from '../../../../Button';
import Input from '../../../../Input';
import PageTitle from '../../../../PageTitle';
import FileUpload from '../../../../FileUpload/file-upload';  // Importando o componente FileUpload
import { useStyles } from './styles';
import { IMaskInputCPF, IMaskInputDate, IMaskInputRG } from '../../../../IMaskInput';
import { format } from 'date-fns';
import SignatureCanvasComponent from '../../../../SignatureCanvas';

export type FormRegisterProps = {
  getForms: GetForms;
  getProgramsAccreditedsByProgramId: GetProgramsAccreditedsByProgramId;
  registerFormAnswers: RegisterFormAnswers;
  getPatientByCpf: GetPatientByCpf;
  getPatientByRg: GetPatientByRg;
  getProgramsPatientsByProgramIdRg: GetProgramsPatientsByProgramIdRg;
  getProgramsPatientsByProgramIdCpf: GetProgramsPatientsByProgramIdCpf;
};

interface Form {
  id: string;
  name: string;
  program: {
    id: string;
  };
}


interface Accredited {
  id: string;
  corporateReason: string;
  programAccreditedId: string;
}

function removeMask(input: string): string {
  return input.replace(/\D/g, ''); // Remove tudo o que não for número
}

export default function FormRegister(
  { 
    getForms, getProgramsAccreditedsByProgramId, registerFormAnswers, getPatientByCpf, getPatientByRg, getProgramsPatientsByProgramIdRg, getProgramsPatientsByProgramIdCpf
  }: FormRegisterProps): JSX.Element {

  const classes = useStyles();
  const formRef = useRef({} as any);
  const { isLoading, showLoading, hideLoading } = useLoading();
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();

  const [forms, setForms] = useState<Form[]>([]);
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedProgramId, setSelectedProgramIdForm] = useState('');
  const [selectedProgramPatientId, setSelectedProgramPatientId] = useState('');

  const [isFieldsVisible, setIsFieldsVisible] = useState(false);
  const [isFieldsEnabled, setIsFieldsEnabled] = useState(false);
  const [cpf, setCpf] = useState('');
  const [rg, setRg] = useState('');
  const [patient, setPatient] = useState<any>(null);
  const [accrediteds, setAccredited] = useState<Accredited[]>([]);
  const [selectedAccredited, setSelectedAccredited] = useState('');
  const [showButtons, setShowButtons] = useState(false); // Estado para mostrar os botões

  const [collapseOpen, setCollapseOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [files, setFiles] = useState<File[]>([]);
  const [programAccreditedId, setProgramAccreditedId] = useState<string>('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signature, setSignature] = useState<string | null>(null);

  const selectedFormData = forms.find(form => form.id === selectedForm);
  const isHemoCueForm = selectedFormData?.name
    .trim()
    .toLocaleLowerCase('pt-BR')
    .includes('hemocue');

  const termPdfUrl = isHemoCueForm
    ? termoHemoCuePDF
    : termoEspirometriaPDF;

  const [isDocumentUploaded, setIsDocumentUploaded] = useState(false);
  const [isSignatureCollected, setIsSignatureCollected] = useState(false);

  const [isFileSelected, setIsFileSelected] = useState(false);

  const [isConcluirEnabled, setIsConcluirEnabled] = useState(false);

  const [isProgramDisabled, setIsProgramDisabled] = useState(false);
  const [shouldFetchPatient, setShouldFetchPatient] = useState(false);

  const handleOpenSignatureDialog = () => {
    setSignatureDialogOpen(true);
  };
  
  const handleCloseSignatureDialog = () => {
    setSignatureDialogOpen(false);
  };
  
  const handleSaveSignature = (signatureData: string) => {
    setSignature(signatureData);
    setFiles([]);
    setIsDocumentUploaded(true);
    setIsSignatureCollected(true);
    setActiveStep(4);
    setIsConcluirEnabled(true);
  };

  const steps = [
    'Selecionar Formulário',
    'Preencher CPF e RG',
    'Selecionar Credenciado',
    'Assinatura do Documento',
    'Concluir',
  ];

  const handleCollapseToggle = () => {
    setCollapseOpen(!collapseOpen);
  };
  
  const handleFormChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedValue = event.target.value as string;
    const selectedFormData = forms.find(
      form => form.id === selectedValue
    );

    setSelectedForm(selectedValue);
    setSelectedProgramIdForm(selectedFormData?.program.id ?? '');
    setIsFieldsVisible(true);
    setActiveStep(1);
  };

  const onChangeCpfInput = (event: React.ChangeEvent<{ value: unknown }>) => {
    const state = event.target.value as string;
    setCpf(state);
    if (rg) setActiveStep(2);
    if (state.length === 14) {
      setShouldFetchPatient(true);
    } else {
      setShouldFetchPatient(false);
    }
  };
  
  const onChangeRgInput = (event: React.ChangeEvent<{ value: unknown }>) => {
    const state = event.target.value as string;
    setRg(state);
    if (cpf) setActiveStep(2);
    if (state.length === 12) {
      setShouldFetchPatient(true);
    } else {
      setShouldFetchPatient(false);
    }
  };    

  const handleAccreditedChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const selectedValue = event.target.value;
    setSelectedAccredited(selectedValue as string);
    const selectedAccredited = accrediteds.find(acc => acc.id === selectedValue);
    if (selectedAccredited) {
      setProgramAccreditedId(selectedAccredited.programAccreditedId);
      setShowButtons(true);
    } else {
      setShowButtons(false);
    }
    setActiveStep(3);
  };
  
  const handleFileChange = (fileContentValue: string, fileNameValue: string) => {
    setFiles(prevFiles => [
      ...prevFiles,
      { content: fileContentValue, name: fileNameValue }
    ]);
    setIsFileSelected(true);
    setActiveStep(4);
    setIsConcluirEnabled(true);
  };
  
  const handleFileRemove = () => {
    setFiles([]);
    setIsFileSelected(false);
    setActiveStep(3); // Volta para o passo anterior no stepper
    setIsConcluirEnabled(false);
  };  
  
  const handleConcluir = () => {
    if (activeStep < 4 || (files.length === 0 && !signature)) {
      toast.error('Por favor, complete todas as etapas antes de concluir.');
      return;
    }
    setActiveStep(5); // Atualiza para o passo "Concluir"
    formRef.current.submitForm(); // Submete o formulário
  };  
  
  const handleSubmit = async (data: any) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    showLoading();
  
    try {
      if (files.length === 0 && !signature) {
        toast.error('Por favor, selecione um arquivo ou faça uma assinatura.');
        return;
      }
  
      const isFileUpload = files.length > 0;
      
      const cleanedData: RegisterFormAnswersParams = {
        acceptedTerms: true,
        formId: selectedForm,
        programPatientId: selectedProgramPatientId,
        programAccreditedId: programAccreditedId,
        fileBase64: isFileUpload ? files.map(file => file.content) : [signature],
        fileName: isFileUpload ? files.map(file => file.name) : ['assinatura.png'],
        termType: isFileUpload ? 'FILE_UPLOADED' : 'SIGNED_ON_APP'
      };
  
      await registerFormAnswers.execute(cleanedData);
  
      toast.success('Coleta de consentimento realizada com sucesso!', {
        toastId: 'success-submit'
      });
  
      push(FORMS_ROUTE);
    } catch (error) {
      console.error('Erro ao coletar consentimento:', error);
      toast.error('Erro ao coletar consentimento. Por favor, tente novamente.');
    } finally {
      hideLoading();
      setIsSubmitting(false);
    }
  };  
  
  useEffect(() => {
    showLoading();
    async function runAsync() {
      try {
        const formsResponse: Form[] = await getForms.execute();

        setForms(formsResponse);
      } catch (err) {
        console.log(err);
        toast.error('Falha ao carregar formulários');
      } finally {
        hideLoading();
      }
    }

    runAsync();
  }, [getForms]);

  useEffect(() => {
    const checkAndFetchData = async () => {
      if (id) {
        setIsFieldsEnabled(true);
        setIsProgramDisabled(true);
        return;
      }
  
      if (shouldFetchPatient && ((cpf && cpf.length === 14) || (rg && rg.length === 12))) {
        let patient = null;
  
        try {
          if (cpf && cpf.length === 14) {
            const cleanCpf = removeMask(cpf);
            patient = await getPatientByCpf.execute({ cpf: cleanCpf });
          } else if (rg && rg.length === 12) {
            const cleanRg = removeMask(rg);
            patient = await getPatientByRg.execute({ rg: cleanRg });
          }
  
          if (patient) {
            await handlePatientData(patient);
          } else {
            toast.warn('Paciente não encontrado! O paciente deve ser previamente cadastrado.');
            setIsFieldsEnabled(false);
            setIsProgramDisabled(false);
          }
        } catch (error) {
          console.error('Erro ao buscar paciente:', error);
          toast.error('Erro ao buscar paciente. Por favor, tente novamente.');
          setIsFieldsEnabled(false);
          setIsProgramDisabled(false);
        }
        setShouldFetchPatient(false);
      } else if (!cpf && !rg) {
        setIsFieldsEnabled(false);
        setIsProgramDisabled(false);
        formRef.current.setData({
          cpf: '',
          rg: '',
          name: '',
          birthDate: '',
        });
      }
    };
  
    checkAndFetchData();
  }, [cpf, rg, id, shouldFetchPatient]);    
  
  const handlePatientData = async (patient: any) => {
    if (patient) {
      let programPatientData: ProgramsPatients | null = null;
  
      try {
        if (patient.cpf && patient.cpf.length === 11) {
          programPatientData = await getProgramsPatientsByProgramIdCpf.execute({ programId: selectedProgramId, cpf: patient.cpf });
        } else if (patient.rg && patient.rg.length === 9) {
          programPatientData = await getProgramsPatientsByProgramIdRg.execute({ programId: selectedProgramId, rg: patient.rg });
        }

        if (programPatientData) {
          setIsFieldsEnabled(true);
          setPatient(programPatientData);
          setSelectedProgramPatientId(programPatientData.id);
  
          const formattedBirthDate = programPatientData.patient.birthDate
            ? format(new Date(programPatientData.patient.birthDate), 'dd/MM/yyyy')
            : '';
  
          formRef.current.setData({
            cpf: patient.cpf,
            rg: patient.rg,
            name: programPatientData.patient.name,
            birthDate: formattedBirthDate,
          });
  
          const programsAccreditedsResponse: ProgramsAccrediteds[] = await getProgramsAccreditedsByProgramId.execute({
            programId: selectedProgramId,
          });
  
          const formattedAccrediteds = programsAccreditedsResponse.map((item: any) => ({
            id: item.accredited.id,
            corporateReason: item.accredited.corporateReason,
            programAccreditedId: item.id
          }));
          
          setAccredited(formattedAccrediteds);
        } else {
          toast.warn('Paciente não encontrado no programa! O paciente deve ser previamente cadastrado no programa.');
          setIsFieldsEnabled(false);
          setIsProgramDisabled(false);
        }
      } catch (error) {
        console.error('Erro ao buscar dados do programa:', error);
        toast.warn('Paciente não encontrado no programa! O paciente deve ser previamente cadastrado no programa.');
        setIsFieldsEnabled(false);
        setIsProgramDisabled(false);
      }
    } else {
      formRef.current.setData({
        cpf: cpf,
        rg: rg,
        name: '',
        birthDate: '',
      });
  
      setIsFieldsEnabled(false);
      setIsProgramDisabled(false);
    }
  };    
  
  return (
    <Box className={classes.container}>
      <PageTitle>{'Coleta de consentimento'}</PageTitle>
      <Stepper activeStep={activeStep} alternativeLabel className={classes.stepper}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <br />
      <Form ref={formRef} onSubmit={handleSubmit}>
        <Paper className={classes.container}>
          <Grid container spacing={2} direction="row">
            <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Formulário</InputLabel>
                <Select
                  label="Formulário"
                  name="formId"
                  onChange={handleFormChange}
                  disabled={isProgramDisabled}
                  value={selectedForm}
                  required={true}
                >
                  {forms.map((form) => (
                    <MenuItem key={form.id} value={form.id}>
                      {form.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {isFieldsVisible && (
              <>
                {/* Campos CPF e RG (editáveis) */}
                <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
                  <Input
                    name="cpf"
                    variant="outlined"
                    label={'CPF Paciente'}
                    className={classes.input}
                    disabled={isLoading}
                    mask={IMaskInputCPF}
                    value={cpf}
                    onChange={onChangeCpfInput}
                    required={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
                  <Input
                    name="rg"
                    variant="outlined"
                    label={'RG Paciente'}
                    className={classes.input}
                    disabled={isLoading}
                    mask={IMaskInputRG}
                    value={rg}
                    onChange={onChangeRgInput}
                    required={true}
                  />
                </Grid>
                {/* Campos Nome e Data de Nascimento (desabilitados e preenchidos se paciente encontrado) */}
                <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
                  <Input
                    name="name"
                    variant="outlined"
                    label={'Nome'}
                    className={classes.input}
                    disabled={true}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
                  <Input
                    name="birthDate"
                    variant="outlined"
                    label={'Data de Nascimento'}
                    className={classes.input}
                    disabled={true}
                    mask={IMaskInputDate}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} className={classes.gridItem}>
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel>Credenciados</InputLabel>
                    <Select
                      label="Credenciados"
                      name="accreditedId"
                      onChange={handleAccreditedChange}
                      disabled={!isFieldsEnabled}
                      value={selectedAccredited}
                      required={true}
                    >
                      {accrediteds.map((accredited) => (
                        <MenuItem key={accredited.id} value={accredited.id}>
                          {accredited.corporateReason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {showButtons && (
                  <Grid item xs={12} container spacing={2} alignItems="center">
                    <Grid item>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleCollapseToggle}
                        disabled={isSignatureCollected}
                      >
                        {collapseOpen ? 'Ocultar Upload' : 'Enviar documento assinado'}
                      </MuiButton>
                    </Grid>
                    <Grid item>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleOpenSignatureDialog}
                        disabled={isFileSelected}
                      >
                        Coletar Assinatura Via Aplicativo
                      </MuiButton>
                    </Grid>
                  </Grid>
                )}

                {collapseOpen && (
                  <Grid item xs={12}>
                    <Collapse in={collapseOpen}>
                      <FileUpload
                        questionCode={123}
                        label="Anexar Arquivo"
                        onChange={handleFileChange}
                        onRemove={handleFileRemove}
                        disabled={isDocumentUploaded}
                      />
                    </Collapse>
                  </Grid>
                )}

              </>
            )}
            <Grid container justify="flex-end" style={{ marginTop: '20px' }}>
              <Grid item>
                <Button type="submit" variant="contained" color="primary" onClick={handleConcluir} disabled={!isConcluirEnabled}>
                  {'Concluir'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Form>
      <SignatureCanvasComponent
        open={signatureDialogOpen}
        onClose={handleCloseSignatureDialog}
        onSave={handleSaveSignature}
        pdfUrl={termPdfUrl}
      />
    </Box>
  );
}
